import type { ServiceDetail } from "./service-details";

/**
 * Detail content for the remaining headline services.
 * Kept in a separate module so `service-details.ts` stays readable.
 */
export const extraServiceDetails: ServiceDetail[] = [
  {
    slug: "accounting",
    name: "Accounting",
    eyebrow: "Service",
    title: "Accrual and cash-basis accounting, run to a standard.",
    intro:
      "Beyond data entry: journal entries, accruals, revenue recognition, and a general ledger structured the way your CPA and investors expect to read it.",
    metaTitle: "Outsourced Accounting Services | Finendra",
    metaDescription:
      "GAAP & IFRS-aligned accrual and cash-basis accounting, journal entries, revenue recognition, and CPA-ready workpapers with a clear monthly delivery schedule.",
    deliverables: [
      {
        group: "Monthly accounting",
        items: [
          "Accrual, prepaid, deferral, and depreciation entries",
          "Revenue recognition aligned to your contracts and policy",
          "Intercompany and multi-entity entries where applicable",
          "General ledger review with supporting schedules",
        ],
      },
      {
        group: "Controls & documentation",
        items: [
          "Documented accounting policy memo for your entity",
          "Balance sheet account reconciliation schedules",
          "Workpaper pack handed to your CPA at year end",
        ],
      },
      {
        group: "Ongoing",
        items: [
          "Named accountant plus reviewer on every entry",
          "Question log answered within one business day",
          "Quarterly policy and structure review",
        ],
      },
    ],
    timeline: [
      {
        when: "Weekly",
        label: "Ledger review",
        detail: "Entries reviewed for coding accuracy and policy alignment.",
      },
      {
        when: "Business day 3",
        label: "Accruals posted",
        detail: "Period entries recorded with supporting schedules attached.",
      },
      {
        when: "Business day 5",
        label: "Books closed",
        detail: "General ledger locked and released to reporting.",
      },
      {
        when: "Quarterly",
        label: "Policy review",
        detail: "Accounting treatment revisited as the business changes.",
      },
    ],
    onboarding: [
      {
        step: "Discovery & access",
        duration: "Days 1–3",
        detail: "Entity structure, policies, and system access reviewed.",
      },
      {
        step: "Policy alignment",
        duration: "Days 3–10",
        detail: "We document how revenue, accruals, and capitalization are treated.",
      },
      {
        step: "First supervised close",
        duration: "Month 1",
        detail: "We run the cycle with your team and clean legacy entries.",
      },
      {
        step: "Steady state",
        duration: "From month 2",
        detail: "Standard 5-business-day accounting cycle.",
      },
    ],
    outcomes: [
      "Financials that hold up under CPA and investor review",
      "Consistent treatment month over month",
      "No year-end scramble to rebuild support",
    ],
    tools: ["QuickBooks Online", "Xero", "Sage Intacct", "Google Sheets", "Notion"],
  },
  {
    slug: "reconciliations",
    name: "Bank & Card Reconciliations",
    eyebrow: "Service",
    title: "Every account reconciled, every period, with proof.",
    intro:
      "Bank, credit card, merchant, and loan accounts reconciled on a fixed cadence — with documented variances instead of unexplained balances.",
    metaTitle: "Bank & Credit Card Reconciliation Services | Finendra",
    metaDescription:
      "Fixed-cadence bank, card, merchant, and loan reconciliations with documented variances and audit-ready support. See deliverables and timelines.",
    deliverables: [
      {
        group: "Monthly reconciliations",
        items: [
          "Bank and credit card accounts reconciled to statements",
          "Merchant processor settlements matched to deposits",
          "Loan, line-of-credit, and interest balances tied out",
          "Outstanding and stale item review",
        ],
      },
      {
        group: "Documentation",
        items: [
          "Reconciliation pack with statements and support attached",
          "Variance log with cause and resolution owner",
          "Sign-off trail from preparer and reviewer",
        ],
      },
      {
        group: "Clean-up",
        items: [
          "Historical unreconciled periods brought current",
          "Duplicate and misposted transactions corrected",
          "Suspense and clearing accounts cleared down",
        ],
      },
    ],
    timeline: [
      {
        when: "Weekly",
        label: "Feed review",
        detail: "Bank feeds checked so nothing is missing before period end.",
      },
      {
        when: "Business day 2",
        label: "Statements gathered",
        detail: "All statements collected and matched to accounts.",
      },
      {
        when: "Business day 3",
        label: "Reconciliations complete",
        detail: "Every account tied out with variances documented.",
      },
      {
        when: "Business day 4",
        label: "Review & sign-off",
        detail: "Reviewer approves and the pack is issued.",
      },
    ],
    onboarding: [
      {
        step: "Account inventory",
        duration: "Days 1–3",
        detail: "Every bank, card, merchant, and loan account catalogued.",
      },
      {
        step: "Access & statements",
        duration: "Days 3–7",
        detail: "Read-only access and statement delivery set up.",
      },
      {
        step: "Backlog clean-up",
        duration: "1–4 weeks",
        detail: "Unreconciled history rebuilt before the live cycle starts.",
      },
      {
        step: "Steady state",
        duration: "From month 2",
        detail: "Reconciliations delivered by business day 3.",
      },
    ],
    outcomes: [
      "No unexplained balances at close",
      "Faster CPA review and audit prep",
      "Fraud and duplicate charges caught early",
    ],
    tools: ["QuickBooks Online", "Xero", "Plaid feeds", "Stripe", "Shopify", "Ramp"],
  },
  {
    slug: "financial-reporting",
    name: "Financial Statements & Reporting",
    eyebrow: "Service",
    title: "Reporting your team actually uses to make decisions.",
    intro:
      "P&L, balance sheet, and cash flow delivered on a fixed date each month, plus the management reporting and commentary behind the numbers.",
    metaTitle: "Financial Statements & Management Reporting | Finendra",
    metaDescription:
      "Monthly P&L, balance sheet, cash flow, KPI dashboards, and budget-vs-actual reporting with written commentary, delivered on a fixed schedule.",
    deliverables: [
      {
        group: "Core statements",
        items: [
          "Profit and loss with prior period and budget comparison",
          "Balance sheet with supporting schedules",
          "Cash flow statement and 13-week cash view",
        ],
      },
      {
        group: "Management reporting",
        items: [
          "KPI dashboard tailored to your business model",
          "Budget vs. actual with variance commentary",
          "Departmental, class, or location-level reporting",
        ],
      },
      {
        group: "Communication",
        items: [
          "Written month-end summary in plain language",
          "Monthly review call with your account lead",
          "Board and lender reporting packs on request",
        ],
      },
    ],
    timeline: [
      {
        when: "Business day 5",
        label: "Books closed",
        detail: "Close completes and reporting inputs are locked.",
      },
      {
        when: "Business day 6",
        label: "Statements built",
        detail: "Statements and dashboards refreshed and reviewed.",
      },
      {
        when: "Business day 7",
        label: "Pack delivered",
        detail: "Reporting pack issued with written commentary.",
      },
      {
        when: "Business day 8–10",
        label: "Review call",
        detail: "Walkthrough of results, variances, and open questions.",
      },
    ],
    onboarding: [
      {
        step: "Reporting discovery",
        duration: "Days 1–5",
        detail: "We define the metrics and audiences that matter to you.",
      },
      {
        step: "Template build",
        duration: "Days 5–14",
        detail: "Statement formats and dashboards built to your structure.",
      },
      {
        step: "First reporting cycle",
        duration: "Month 1",
        detail: "Pack delivered and refined against your feedback.",
      },
      {
        step: "Steady state",
        duration: "From month 2",
        detail: "Reporting issued by business day 7 every month.",
      },
    ],
    outcomes: [
      "Decisions made on current numbers, not last quarter's",
      "Lender and investor requests answered same week",
      "One consistent source of truth across the team",
    ],
    tools: ["QuickBooks Online", "Xero", "Fathom", "Looker Studio", "Google Sheets", "Excel"],
  },
  {
    slug: "cfo-controller-support",
    name: "CFO & Controller Support",
    eyebrow: "Service",
    title: "Senior financial oversight, without a senior salary.",
    intro:
      "Forecasting, controls, and decision support from experienced controllers and fractional CFOs who plug into your leadership rhythm.",
    metaTitle: "Fractional CFO & Controller Services | Finendra",
    metaDescription:
      "Cash flow forecasting, budgeting, internal controls, fundraising and diligence support from experienced controllers and fractional CFOs.",
    deliverables: [
      {
        group: "Planning & forecasting",
        items: [
          "Rolling 12-month financial model and scenario planning",
          "13-week cash flow forecast maintained weekly",
          "Annual budget build and quarterly reforecast",
        ],
      },
      {
        group: "Controls & oversight",
        items: [
          "Internal control review with documented remediation plan",
          "Approval matrix and segregation-of-duties design",
          "Close and reporting quality review each period",
        ],
      },
      {
        group: "Strategic support",
        items: [
          "Board and investor reporting packs",
          "Fundraising and due-diligence data room support",
          "Pricing, margin, and unit economics analysis",
        ],
      },
    ],
    timeline: [
      {
        when: "Weekly",
        label: "Cash review",
        detail: "13-week forecast refreshed and flagged risks escalated.",
      },
      {
        when: "Monthly",
        label: "Results review",
        detail: "Controller review of close quality plus a leadership walkthrough.",
      },
      {
        when: "Quarterly",
        label: "Reforecast",
        detail: "Model updated and plan re-baselined with leadership.",
      },
      {
        when: "Annually",
        label: "Budget & controls",
        detail: "Full budget build and internal control reassessment.",
      },
    ],
    onboarding: [
      {
        step: "Financial diagnostic",
        duration: "Days 1–10",
        detail: "Review of historicals, controls, and reporting quality.",
      },
      {
        step: "Model build",
        duration: "Weeks 2–4",
        detail: "Forecast model and cash view built with your assumptions.",
      },
      {
        step: "Cadence set",
        duration: "Month 1",
        detail: "Weekly cash and monthly leadership rhythm established.",
      },
      {
        step: "Steady state",
        duration: "From month 2",
        detail: "Ongoing oversight, forecasting, and decision support.",
      },
    ],
    outcomes: [
      "Cash runway you can see months ahead",
      "Diligence-ready financials before you need them",
      "Senior judgment on hand without a full-time hire",
    ],
    tools: ["Excel", "Google Sheets", "Fathom", "QuickBooks Online", "Xero", "Notion"],
  },
  {
    slug: "quickbooks-xero",
    name: "QuickBooks & Xero Management",
    eyebrow: "Service",
    title: "A clean accounting stack, set up and maintained.",
    intro:
      "Setup, migration, integrations, and ongoing administration of QuickBooks Online or Xero — so your systems support the close instead of slowing it down.",
    metaTitle: "QuickBooks & Xero Setup, Migration & Management | Finendra",
    metaDescription:
      "QuickBooks Online and Xero setup, migration, app integrations, and ongoing administration with automated workflows and clean data hygiene.",
    deliverables: [
      {
        group: "Setup & migration",
        items: [
          "New file setup or migration between QuickBooks and Xero",
          "Chart of accounts, classes, locations, and tracking design",
          "Historical data import with balance validation",
        ],
      },
      {
        group: "Integrations & automation",
        items: [
          "Bank feed, payment, payroll, and commerce app connections",
          "Automated bill capture and approval workflows",
          "Recurring transaction and rule configuration",
        ],
      },
      {
        group: "Ongoing administration",
        items: [
          "User access and permission reviews",
          "Data hygiene checks and duplicate cleanup",
          "Release and feature updates applied to your workflow",
        ],
      },
    ],
    timeline: [
      {
        when: "Week 1",
        label: "System audit",
        detail: "Current file, apps, and workflows reviewed and documented.",
      },
      {
        when: "Week 2",
        label: "Build or migrate",
        detail: "File configured or migrated with balances validated.",
      },
      {
        when: "Week 3",
        label: "Integrations live",
        detail: "Apps connected, rules set, and automations tested.",
      },
      {
        when: "Ongoing",
        label: "Administration",
        detail: "Monthly hygiene checks and quarterly access reviews.",
      },
    ],
    onboarding: [
      {
        step: "Discovery",
        duration: "Days 1–3",
        detail: "Inventory of your current stack and pain points.",
      },
      {
        step: "Design",
        duration: "Days 3–7",
        detail: "Target account structure and app architecture agreed.",
      },
      {
        step: "Implementation",
        duration: "Weeks 2–3",
        detail: "Build, migrate, validate, and train your team.",
      },
      {
        step: "Steady state",
        duration: "From week 4",
        detail: "Ongoing administration and support.",
      },
    ],
    outcomes: [
      "One reliable system instead of scattered spreadsheets",
      "Less manual entry through automation",
      "Faster close because the data arrives clean",
    ],
    tools: ["QuickBooks Online", "Xero", "Dext", "Hubdoc", "Bill.com", "Gusto", "Stripe"],
  },
  {
    slug: "catch-up-cleanup",
    name: "Catch-up & Clean-up",
    eyebrow: "Service",
    title: "Backlogged books rebuilt and brought fully current.",
    intro:
      "Months or years behind? We reconstruct the history, reconcile every account, correct the errors, and hand you a ledger you can file and finance from.",
    metaTitle: "Bookkeeping Catch-up & Clean-up Services | Finendra",
    metaDescription:
      "Historical bookkeeping rebuilds, reconciliations, and error correction for backlogged books — with a fixed-scope plan and tax-ready deliverables.",
    deliverables: [
      {
        group: "Assessment",
        items: [
          "Diagnostic of every open period and account",
          "Fixed-scope catch-up plan with timeline and cost",
          "Risk list covering tax, lender, and filing exposure",
        ],
      },
      {
        group: "Rebuild",
        items: [
          "Historical transactions recorded and categorized",
          "All bank, card, and merchant accounts reconciled",
          "Duplicate, misposted, and suspense entries corrected",
          "Missing accruals, payroll, and loan entries posted",
        ],
      },
      {
        group: "Handover",
        items: [
          "Period-by-period financial statements",
          "CPA-ready workpaper pack",
          "Transition into ongoing monthly bookkeeping",
        ],
      },
    ],
    timeline: [
      {
        when: "Days 1–5",
        label: "Diagnostic",
        detail: "Scope, gaps, and risks documented with a fixed quote.",
      },
      {
        when: "Weeks 1–2",
        label: "Data gathering",
        detail: "Statements, payroll records, and documents collected.",
      },
      {
        when: "Weeks 2–6",
        label: "Rebuild",
        detail: "Periods reconstructed and reconciled, oldest to newest.",
      },
      {
        when: "Final week",
        label: "Review & handover",
        detail: "Statements issued and the live cycle handed over.",
      },
    ],
    onboarding: [
      {
        step: "Diagnostic call",
        duration: "Days 1–2",
        detail: "We review the file and confirm how far behind you are.",
      },
      {
        step: "Fixed-scope quote",
        duration: "Days 3–5",
        detail: "Written plan, timeline, and price before work starts.",
      },
      {
        step: "Rebuild sprint",
        duration: "2–8 weeks",
        detail: "Duration depends on volume and periods outstanding.",
      },
      {
        step: "Ongoing service",
        duration: "After handover",
        detail: "Move into monthly bookkeeping and close.",
      },
    ],
    outcomes: [
      "Filings and extensions resolved with accurate numbers",
      "Lender and investor requests finally answerable",
      "A clean baseline to run monthly from",
    ],
    tools: ["QuickBooks Online", "Xero", "Dext", "Excel", "Google Sheets"],
  },
  {
    slug: "finance-process-outsourcing",
    name: "Finance Process Outsourcing",
    eyebrow: "Service",
    title: "Your entire back office, run as one dependable function.",
    intro:
      "A dedicated finance pod covering bookkeeping, AP, AR, payroll, close, and reporting — documented in SOPs so the process outlives any individual.",
    metaTitle: "Finance & Accounting Process Outsourcing (FPO) | Finendra",
    metaDescription:
      "A dedicated offshore finance pod running bookkeeping, AP, AR, payroll, close, and reporting under documented SOPs with clear SLAs and oversight.",
    deliverables: [
      {
        group: "The pod",
        items: [
          "Dedicated bookkeeper, accountant, and reviewer assigned to you",
          "Named account lead as your single point of contact",
          "Backup coverage so nothing stops during leave",
        ],
      },
      {
        group: "Process & controls",
        items: [
          "Documented SOPs for every recurring finance task",
          "Approval matrix and segregation of duties",
          "SLA-tracked turnaround times with monthly reporting",
        ],
      },
      {
        group: "Scope covered",
        items: [
          "Bookkeeping, reconciliations, and month-end close",
          "AP processing and AR collections",
          "Payroll coordination and compliance support",
          "Management reporting and CPA liaison",
        ],
      },
    ],
    timeline: [
      {
        when: "Daily",
        label: "Transaction operations",
        detail: "Bookkeeping, AP, and AR queues worked every business day.",
      },
      {
        when: "Weekly",
        label: "Ops review",
        detail: "SLA check, cash view, and open-item digest to your team.",
      },
      {
        when: "Business day 5",
        label: "Close complete",
        detail: "Books closed on the agreed calendar.",
      },
      {
        when: "Monthly",
        label: "Service review",
        detail: "Reporting pack, SLA performance, and process improvements.",
      },
    ],
    onboarding: [
      {
        step: "Process mapping",
        duration: "Weeks 1–2",
        detail: "Current workflows documented and target state agreed.",
      },
      {
        step: "SOP build & transition",
        duration: "Weeks 2–4",
        detail: "SOPs written, access granted, and the pod trained.",
      },
      {
        step: "Parallel run",
        duration: "Month 1",
        detail: "We run alongside your team before taking full ownership.",
      },
      {
        step: "Steady state",
        duration: "From month 2",
        detail: "Full ownership under agreed SLAs and reporting.",
      },
    ],
    outcomes: [
      "One accountable team instead of scattered vendors",
      "Process continuity independent of staff turnover",
      "Materially lower cost than an in-house department",
    ],
    tools: ["QuickBooks Online", "Xero", "Bill.com", "Gusto", "Ramp", "Slack", "Notion"],
  },
];
